import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Archive, Camera, Download, Edit, FileText, Save, Shield, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { AiSurface } from '@/components/ui/ai-surface';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import {
  usePulseListQuery,
  useDraftPulseMutation,
  useEditPulseMutation,
  usePublishPulseMutation,
  pulseCitationToChip,
  pulseEvidenceSummary,
  pulseHasNarrative,
  type Pulse,
  type PulseCitation,
  type PulseEvidence,
  type PulsePosture,
} from '@/lib/api/pulse';
import {
  usePulsePostureHistoryQuery,
  POSTURE_HISTORY_RANGES,
  type PostureHistoryRange,
  type PostureHistoryPosture,
} from '@/lib/api/pulse-posture-history';
import { ContractHeader } from '@/components/contract/contract-header';
import { ContractAttentionPanel } from '@/components/contract/attention-panel';
import { cn } from '@/lib/utils/cn';
import { formatDateTime, formatDate } from '@/lib/utils/format';
import { displayAiText } from '@/lib/utils/ai-text';
import { labelledError } from '@/lib/api/errors';
import { formatCycleLabel, pluralize, weekCycleLabel } from '@/lib/contract-labels';

/** The pulse forms' field labels, so a validation error names the field (C2-31). */
const PULSE_FIELDS: Record<string, string> = {
  cycle_label: 'Pulse period',
  narrative_text: 'Narrative',
  overall_posture: 'Posture',
  override: 'Posture override reason',
  narrative_citations: 'Sources',
};

/** Why Publish and Edit are unavailable on a draft without a narrative (C2-12). */
const NO_NARRATIVE_REASON =
  'Nothing to publish yet: this draft has no narrative. Draft the pulse again once records are approved.';

/**
 * /contract/pulse — C20-133 prototype-parity rewrite (T-0103 / E06-F07).
 *
 * Visual contract: `screens-modules-tabs.js → contractPulse()` (lines
 * 673-748). Layout:
 *
 *   <ContractHeader actions="Pulse history · Edit draft · Publish to PDF" />
 *   <PostureChart>             Recharts BarChart over pulse_posture_snapshots
 *                              (T-0131 / PR #144) — backed by the
 *                              deterministic posture rollup, NOT the LLM.
 *   <AiSurface latest>         AI-drafted pulse card (lavender background)
 *     ↳ Narrative text + edit mode (inline textarea + posture override picker)
 *     ↳ Divergent items callout (E04-F06 — vendor vs. Avero, this period)
 *     ↳ Citations row (each chip opens CitationSourceModal)
 *   <PulseHistory>             Table of prior pulses; status / posture / date
 *
 * Wire-up matrix:
 *   - Pulse list                 → usePulseListQuery (T-0069)
 *   - Latest draft / publish     → useDraftPulseMutation / useEditPulseMutation
 *                                  / usePublishPulseMutation (T-0069)
 *   - Posture trend chart        → usePulsePostureHistoryQuery (T-0131)
 *   - Citation chip → modal      → CitationChip + CitationSourceModal
 *   - "Edit draft" / "Publish"   → inline editor + publish button
 *   - "Pulse history"            → defers to the history table below
 *
 * Backend gaps surfaced as Pending (never faked):
 *   - Per-pulse confidence value isn't a backend field — we band by
 *     citation count in `deriveConfidence` (see helper) so the AI
 *     confidence chip always renders.
 *   - Record-level lock state (E04-F01 lock banner) — the backend exposes
 *     this for some surfaces but not on the pulse DTO yet; we omit the
 *     banner rather than invent a lock.
 *   - "Vendor vs Avero divergent" deliverable list — derived from the
 *     verification surface (T-0037) cross-referenced with the EDL list.
 *     Until those joins are exposed in a single endpoint, the callout
 *     surfaces only what the latest pulse's `narrative_sections` carries
 *     under the `divergent_items` key (the T-0069 narrative builder
 *     populates this); empty when the pipeline didn't emit any.
 */
export const Route = createFileRoute('/engagements/$engagementId/contract/pulse')({
  component: ContractPulseTab,
});

function ContractPulseTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/contract/pulse' });
  const list = usePulseListQuery(engagementId, { limit: 20 });
  const draftMutation = useDraftPulseMutation(engagementId);
  const editMutation = useEditPulseMutation(engagementId);
  const publishMutation = usePublishPulseMutation(engagementId);

  // Sort pulses oldest → newest for the posture chart. The list API
  // returns paginated results in created_at DESC by default; we
  // reverse here for the chart's natural left-to-right reading.
  const pulsesSorted = useMemo<Pulse[]>(() => {
    const items = list.data?.items ?? [];
    return [...items].sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [list.data]);

  // The "latest" pulse is the most recent by created_at — the one the
  // header AI surface targets. Prefer a draft if one exists at the
  // top so the consultant can keep iterating; otherwise show the most
  // recent published as a read-only headline.
  const latest = useMemo<Pulse | undefined>(() => {
    const items = list.data?.items ?? [];
    const draft = items.find((p) => p.status === 'draft');
    return draft ?? items[0];
  }, [list.data]);

  const publishedCount = pulsesSorted.filter((p) => p.status === 'published').length;
  const draftCount = pulsesSorted.filter((p) => p.status === 'draft').length;

  return (
    <div className="space-y-6">
      <ContractHeader
        engagementId={engagementId}
        active="pulse"
        meta={
          <span>
            {pulsesSorted.length} {pulsesSorted.length === 1 ? 'pulse' : 'pulses'} ·{' '}
            {publishedCount} published · {draftCount} draft
          </span>
        }
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => draftMutation.mutate({ input: { cycle_label: weekCycleLabel() } })}
              disabled={draftMutation.isPending}
            >
              {draftMutation.isPending ? (
                <Spinner />
              ) : (
                <FileText className="h-4 w-4" aria-hidden="true" />
              )}
              Draft pulse
            </Button>
            {latest && latest.status !== 'published' ? (
              <Button
                type="button"
                size="sm"
                className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
                onClick={() => publishMutation.mutate({ pulseId: latest.pulse_id })}
                disabled={publishMutation.isPending || !pulseHasNarrative(latest)}
                title={pulseHasNarrative(latest) ? undefined : NO_NARRATIVE_REASON}
                data-open-pdf={engagementId}
              >
                {publishMutation.isPending ? (
                  <Spinner />
                ) : (
                  <Download className="h-4 w-4" aria-hidden="true" />
                )}
                Publish to PDF
              </Button>
            ) : null}
          </>
        }
      />

      <ContractAttentionPanel engagementId={engagementId} hideTarget="pulse" />

      {draftMutation.error ? (
        <ErrorMessage error={labelledError(draftMutation.error, PULSE_FIELDS)} />
      ) : null}

      {list.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading pulses…
        </div>
      ) : list.error ? (
        <ErrorMessage error={list.error} />
      ) : !latest ? (
        <EmptyState
          title="No pulses yet"
          description="Draft a pulse to kick off the AI narrative pipeline. The consultant can edit + override posture before publishing the snapshot."
          action={
            <Button
              type="button"
              size="sm"
              onClick={() => draftMutation.mutate({ input: { cycle_label: weekCycleLabel() } })}
              disabled={draftMutation.isPending}
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              Draft pulse
            </Button>
          }
        />
      ) : (
        <>
          <PostureChart engagementId={engagementId} pulses={pulsesSorted} />
          <LatestPulseSurface
            engagementId={engagementId}
            pulse={latest}
            onEdit={(pulseId, input) => editMutation.mutate({ pulseId, input })}
            onPublish={(pulseId) => publishMutation.mutate({ pulseId })}
            editPending={editMutation.isPending}
            publishPending={publishMutation.isPending}
            editError={editMutation.error}
            publishError={publishMutation.error}
          />
          <PulseHistory pulses={pulsesSorted} />
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Posture chart
// ===========================================================================

interface ChartDatum {
  cycle: string;
  /** Numeric posture score for the Recharts y-axis. */
  posture: number;
  /** Original chart-vocabulary label for the tooltip. */
  postureLabel: PostureHistoryPosture;
  contributingSignalsCount: number;
  capturedAt: string;
  snapshotId: string;
  pulseId: string | null;
}

const POSTURE_HISTORY_SCORES: Record<PostureHistoryPosture, number> = {
  green: 3,
  yellow_divergent: 2,
  red: 1,
  grey: 0,
  unassessed: 0,
};

const POSTURE_HISTORY_DISPLAY: Record<PostureHistoryPosture, string> = {
  green: 'green',
  yellow_divergent: 'yellow (divergent)',
  red: 'red',
  grey: 'grey',
  unassessed: 'unassessed',
};

function postureHistoryColor(p: PostureHistoryPosture): string {
  switch (p) {
    case 'green':
      return 'var(--posture-healthy)';
    case 'yellow_divergent':
      return 'var(--posture-at-risk)';
    case 'red':
      return 'var(--posture-critical)';
    case 'grey':
    case 'unassessed':
    default:
      return 'var(--posture-unassessed)';
  }
}

const RANGE_LABEL: Record<PostureHistoryRange, string> = {
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
};

function PostureChart({ engagementId, pulses }: { engagementId: string; pulses: Pulse[] }) {
  const [range, setRange] = useState<PostureHistoryRange>('90d');
  const history = usePulsePostureHistoryQuery(engagementId, range);

  // Cross-reference snapshots → pulses to recover cycle_label for the
  // x-axis. Snapshots created before this pulse list was paginated land
  // in the fallback "MM-DD" label.
  const pulseById = useMemo(() => {
    const m = new Map<string, Pulse>();
    for (const p of pulses) m.set(p.pulse_id, p);
    return m;
  }, [pulses]);

  const data = useMemo<ChartDatum[]>(() => {
    const items = history.data?.items ?? [];
    return items.map((s) => {
      const pulse = s.pulse_id ? pulseById.get(s.pulse_id) : undefined;
      const cycle = pulse ? formatCycleLabel(pulse.cycle_label) : formatDate(s.captured_at);
      return {
        cycle,
        posture: POSTURE_HISTORY_SCORES[s.posture],
        postureLabel: s.posture,
        contributingSignalsCount: s.contributing_signals_count,
        capturedAt: s.captured_at,
        snapshotId: s.snapshot_id,
        pulseId: s.pulse_id,
      };
    });
  }, [history.data, pulseById]);

  if (history.isLoading && data.length === 0) {
    return null;
  }
  if (history.error) {
    return <ErrorMessage error={history.error} />;
  }
  if (data.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h2 className="text-h3 font-semibold text-ink-900">Posture trend</h2>
            <p className="text-meta text-ink-500">
              Taken from the posture recorded at each pulse. 3 = Green, 2 = Yellow (divergent), 1 =
              Red, 0 = Grey / Unassessed. The posture is calculated from the records, never written
              by the AI.
            </p>
          </div>
          <div
            className="flex items-center gap-1 rounded-card border border-ink-100 bg-paper-50 p-0.5"
            role="radiogroup"
            aria-label="Posture history range"
          >
            {POSTURE_HISTORY_RANGES.map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={range === r}
                onClick={() => setRange(r)}
                className={cn(
                  'inline-flex h-7 items-center rounded-card px-2 text-meta font-medium transition-colors',
                  range === r
                    ? 'bg-ink-900 text-paper-0'
                    : 'text-ink-700 hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                )}
              >
                {RANGE_LABEL[r]}
              </button>
            ))}
          </div>
        </div>
        <div className="h-48 w-full" role="img" aria-label="Pulse posture trend chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--ink-100)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="cycle" stroke="var(--ink-500)" tick={{ fontSize: 11 }} interval={0} />
              <YAxis
                stroke="var(--ink-500)"
                tick={{ fontSize: 11 }}
                domain={[0, 3]}
                ticks={[0, 1, 2, 3]}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--paper-0)',
                  border: '1px solid var(--ink-100)',
                  fontSize: 12,
                }}
                formatter={(value: number, _name, item) => {
                  const d = item.payload as ChartDatum;
                  const count = d.contributingSignalsCount;
                  return [
                    `${POSTURE_HISTORY_DISPLAY[d.postureLabel]} (${value}) · ${count} signal${count === 1 ? '' : 's'}`,
                    'Posture',
                  ];
                }}
                labelFormatter={(label) => `Cycle: ${String(label)}`}
              />
              <Bar
                dataKey="posture"
                radius={[3, 3, 0, 0]}
                fill="var(--posture-healthy)"
                shape={(props: unknown) => {
                  // Recharts passes the bar `props` with payload — we
                  // recolor per-bar by posture rather than using a
                  // single static fill. We must accept the loose Recharts
                  // shape type via `unknown` and narrow.
                  const p = props as {
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                    payload: ChartDatum;
                  };
                  return (
                    <rect
                      x={p.x}
                      y={p.y}
                      width={p.width}
                      height={p.height}
                      rx={3}
                      ry={3}
                      fill={postureHistoryColor(p.payload.postureLabel)}
                    />
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <dl className="sr-only">
          {data.map((d) => (
            <div key={d.snapshotId}>
              <dt>{d.cycle}</dt>
              <dd>
                Posture {POSTURE_HISTORY_DISPLAY[d.postureLabel]}; {d.contributingSignalsCount}{' '}
                contributing signals; captured {formatDateTime(d.capturedAt)}.
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Latest pulse — AI surface + inline editor + publish flow
// ===========================================================================

interface LatestPulseProps {
  engagementId: string;
  pulse: Pulse;
  onEdit: (pulseId: string, input: EditInput) => void;
  onPublish: (pulseId: string) => void;
  editPending: boolean;
  publishPending: boolean;
  editError: unknown;
  publishError: unknown;
}

interface EditInput {
  narrative_text?: string;
  overall_posture?: PulsePosture;
}

function LatestPulseSurface({
  engagementId,
  pulse,
  onEdit,
  onPublish,
  editPending,
  publishPending,
  editError,
  publishError,
}: LatestPulseProps) {
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(pulse.narrative_text ?? '');
  const [draftPosture, setDraftPosture] = useState<PulsePosture | null>(
    pulse.overall_posture ?? null,
  );
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);

  // When the pulse changes (e.g. after a fresh draft) reset the
  // editor's local state to the new payload.
  useEffect(() => {
    setDraftText(pulse.narrative_text ?? '');
    setDraftPosture(pulse.overall_posture ?? null);
    setEditing(false);
  }, [pulse.pulse_id, pulse.narrative_text, pulse.overall_posture]);

  const chipData = useMemo<CitationChipData[]>(
    () => (pulse.narrative_citations ?? []).map((c, i) => pulseCitationToChip(c, i)),
    [pulse.narrative_citations],
  );

  const isPublished = pulse.status === 'published';
  // C2-12: a refusal or an empty draft offers neither Edit nor Publish.
  const hasNarrative = pulseHasNarrative(pulse);

  const onSave = () => {
    const input: EditInput = {};
    if (draftText !== (pulse.narrative_text ?? '')) input.narrative_text = draftText;
    if (draftPosture && draftPosture !== pulse.overall_posture)
      input.overall_posture = draftPosture;
    if (Object.keys(input).length === 0) {
      setEditing(false);
      return;
    }
    onEdit(pulse.pulse_id, input);
    setEditing(false);
  };

  const onCancel = () => {
    setDraftText(pulse.narrative_text ?? '');
    setDraftPosture(pulse.overall_posture ?? null);
    setEditing(false);
  };

  // Divergent items — sourced from pulse.narrative_sections.divergent_items
  // (the T-0069 narrative builder populates this). When absent the callout
  // is hidden rather than invented.
  const divergentItems = useMemo(() => readDivergentItems(pulse), [pulse]);

  return (
    <AiSurface
      as="div"
      className="rounded-card border-l-2 border-y-0 border-r-0 px-5 py-4"
      role="article"
      aria-label="AI-drafted pulse report"
    >
      <div className="mb-3 flex flex-wrap items-start gap-3">
        <div className="flex-1 space-y-1">
          <div className="text-meta font-semibold uppercase tracking-[0.04em] text-ai-ink">
            AI-drafted · pulse report
          </div>
          <div className="text-h3 font-semibold text-ink-900">
            {formatCycleLabel(pulse.cycle_label)}
          </div>
          <div className="text-meta text-ink-500">
            {pulse.status === 'draft'
              ? `Draft · last updated ${formatDateTime(pulse.updated_at)}`
              : pulse.status === 'publishing'
                ? 'Publishing…'
                : `Published ${formatDateTime(pulse.published_at)}`}
          </div>
          {pulse.evidence ? (
            // The gate's counts (C2-11): what this draft was written from.
            <div className="text-meta text-ink-500">
              Drawn from {pulseEvidenceSummary(pulse.evidence)}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-meta text-ink-500">
            {pulse.is_safe_refusal
              ? 'No narrative drafted'
              : pluralize(pulse.narrative_citations?.length ?? 0, 'cited source', 'cited sources')}
          </span>
          {pulse.overall_posture ? (
            <PostureChip status={pulsePostureToStatus(pulse.overall_posture)}>
              {capitalize(pulse.overall_posture)}
            </PostureChip>
          ) : (
            <PostureChip status="unassessed">Unassessed</PostureChip>
          )}
          {!isPublished && !editing ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              disabled={!hasNarrative}
              title={hasNarrative ? undefined : NO_NARRATIVE_REASON}
              data-open-override={pulse.pulse_id}
            >
              <Edit className="h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
          ) : null}
          {!isPublished ? (
            <Button
              type="button"
              size="sm"
              onClick={() => onPublish(pulse.pulse_id)}
              disabled={publishPending || editing || !hasNarrative}
              title={hasNarrative ? undefined : NO_NARRATIVE_REASON}
            >
              {publishPending ? <Spinner /> : <Download className="h-4 w-4" aria-hidden="true" />}
              Publish
            </Button>
          ) : pulse.pdf_report_id ? (
            <Button type="button" variant="outline" size="sm" data-open-pdf={pulse.pdf_report_id}>
              <Camera className="h-4 w-4" aria-hidden="true" />
              View snapshot
            </Button>
          ) : null}
        </div>
      </div>

      {pulse.is_safe_refusal ? (
        <RefusalCard
          outcome={pulse.narrative_outcome ?? 'no_evidence'}
          text={pulse.narrative_text}
          evidence={pulse.evidence ?? null}
        />
      ) : editing ? (
        <EditorPanel
          text={draftText}
          posture={draftPosture}
          onTextChange={setDraftText}
          onPostureChange={setDraftPosture}
          onSave={onSave}
          onCancel={onCancel}
          saving={editPending}
        />
      ) : (
        <p className="whitespace-pre-wrap text-body leading-[22px] text-ink-700">
          {pulse.narrative_text ? (
            // C2-31 / C2-33: the ids the model copied in and its Markdown
            // markers are display noise; the stored text is unchanged.
            displayAiText(pulse.narrative_text)
          ) : (
            <span className="text-ink-500">No narrative drafted yet. Use Draft pulse above.</span>
          )}
        </p>
      )}

      {editError ? <ErrorMessage error={labelledError(editError, PULSE_FIELDS)} /> : null}
      {publishError ? <ErrorMessage error={labelledError(publishError, PULSE_FIELDS)} /> : null}
      {!isPublished && !hasNarrative && !pulse.is_safe_refusal ? (
        // A refusal explains itself in its card; an empty draft says why here.
        <p className="mt-2 text-meta text-ink-500">{NO_NARRATIVE_REASON}</p>
      ) : null}

      {divergentItems.length > 0 ? <DivergentCallout items={divergentItems} /> : null}

      {chipData.length > 0 ? (
        <div className="mt-4 border-t border-ai-surface-border pt-3">
          <div className="mb-2 text-meta font-semibold uppercase tracking-[0.04em] text-ai-ink">
            Sources · {chipData.length}
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {chipData.map((c) => (
              <li key={c.id}>
                <CitationChip cite={c} onOpen={() => setOpenCite(c)} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {openCite ? (
        <CitationSourceModal
          engagementId={engagementId}
          cite={openCite}
          open={openCite != null}
          onOpenChange={(open) => {
            if (!open) setOpenCite(null);
          }}
        />
      ) : null}
    </AiSurface>
  );
}

function EditorPanel({
  text,
  posture,
  onTextChange,
  onPostureChange,
  onSave,
  onCancel,
  saving,
}: {
  text: string;
  posture: PulsePosture | null;
  onTextChange: (v: string) => void;
  onPostureChange: (v: PulsePosture) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div className="space-y-3">
      <textarea
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        rows={8}
        aria-label="Pulse narrative"
        className="w-full rounded-card border border-ai-surface-border bg-paper-0 px-3 py-2 text-body leading-[22px] text-ink-700 focus:outline-none focus:ring-2 focus:ring-ai-surface-border"
      />
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-meta uppercase tracking-wide text-ai-ink">Posture override</span>
        <div role="radiogroup" aria-label="Posture" className="flex gap-1">
          {(['green', 'yellow', 'red'] as const).map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={posture === p}
              onClick={() => onPostureChange(p)}
              className={
                posture === p
                  ? 'rounded-full bg-ink-900 px-3 py-1 text-meta font-medium text-paper-0'
                  : 'rounded-full border border-ai-surface-border px-3 py-1 text-meta font-medium text-ink-700 hover:bg-paper-0'
              }
            >
              {capitalize(p)}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={saving}>
            <X className="h-4 w-4" aria-hidden="true" />
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={onSave} disabled={saving}>
            {saving ? <Spinner /> : <Save className="h-4 w-4" aria-hidden="true" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function RefusalCard({
  outcome,
  text,
  evidence,
}: {
  outcome: string;
  text: string | null;
  evidence: PulseEvidence | null;
}) {
  // An empty-data refusal is the evidence gate's, decided in code before
  // any model call: say what it counted rather than echo a model refusal.
  const gateRefused = outcome === 'empty_data' && evidence !== null && !evidence.sufficient;
  return (
    <div
      role="status"
      className="rounded-card border border-dashed border-ai-surface-border bg-paper-0 p-4 text-body text-ink-700"
    >
      <div className="text-meta font-semibold uppercase tracking-wide text-ai-ink">
        {gateRefused ? 'Nothing to draft from yet' : `AI refused · ${outcome.replace(/_/g, ' ')}`}
      </div>
      <p className="mt-2 whitespace-pre-wrap">
        {gateRefused
          ? 'The pulse is written from approved contract facts, deliverables, checkpoints and deviations, and this engagement has none yet. Approve them in AI review, then draft again.'
          : (text ??
            'The pulse pipeline declined to generate a narrative. Try adding source documents or rerunning the draft.')}
      </p>
    </div>
  );
}

// ===========================================================================
// Divergent items callout (prototype `.divergent-callout`, lines 710-717)
// ===========================================================================

interface DivergentItem {
  label: string;
  vendor: string;
  avero: string;
  note?: string;
}

function readDivergentItems(p: Pulse): DivergentItem[] {
  const sections = p.narrative_sections;
  if (!sections || typeof sections !== 'object') return [];
  const raw = (sections as Record<string, unknown>)['divergent_items'];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const r = row as Record<string, unknown>;
      const label = typeof r['label'] === 'string' ? r['label'] : null;
      const vendor = typeof r['vendor'] === 'string' ? r['vendor'] : null;
      const avero = typeof r['avero'] === 'string' ? r['avero'] : null;
      if (!label || !vendor || !avero) return null;
      const note = typeof r['note'] === 'string' ? r['note'] : undefined;
      return { label, vendor, avero, ...(note !== undefined ? { note } : {}) };
    })
    .filter((x): x is DivergentItem => x !== null);
}

function DivergentCallout({ items }: { items: DivergentItem[] }) {
  return (
    <div className="mt-4 rounded-card border border-ai-surface-border bg-paper-0 p-3">
      <div className="flex items-center gap-2 text-meta font-semibold uppercase tracking-[0.04em] text-ai-ink">
        <Shield className="h-3 w-3" aria-hidden="true" />
        Vendor vs. Avero — divergent items this period
      </div>
      <p className="mt-1 text-meta text-ink-500">
        Where the vendor's report and Avero's verification disagree, so the steering committee reads
        both.
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-body-sm text-ink-700">
        {items.map((it, i) => (
          <li key={`${it.label}-${i}`}>
            <strong className="text-ink-900">{it.label}</strong> — vendor reports{' '}
            <em>{it.vendor}</em>; Avero verifies <em>{it.avero}</em>
            {it.note ? `. ${it.note}` : '.'}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ===========================================================================
// History
// ===========================================================================

function PulseHistory({ pulses }: { pulses: Pulse[] }) {
  // History is most-recent first.
  const reversed = useMemo(() => [...pulses].reverse(), [pulses]);
  if (reversed.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-0">
        <div className="border-b border-ink-100 px-4 py-3">
          <h2 className="text-h3 font-semibold text-ink-900">Pulse history</h2>
          <p className="text-meta text-ink-500">
            Publishing a pulse freezes a snapshot of the contract and renders it as a firm-branded
            PDF.
          </p>
        </div>
        <ul className="divide-y divide-ink-100" role="list">
          {reversed.map((p) => (
            <li
              key={p.pulse_id}
              className="flex flex-wrap items-center gap-3 px-4 py-3 text-body-sm"
            >
              {/* C2-31: the period the pulse covers, not its id. */}
              <span className="flex-1 font-medium text-ink-900">
                {formatCycleLabel(p.cycle_label)}
              </span>
              {p.overall_posture ? (
                <PostureChip status={pulsePostureToStatus(p.overall_posture)}>
                  {capitalize(p.overall_posture)}
                </PostureChip>
              ) : null}
              <span
                className={
                  p.status === 'published'
                    ? 'rounded-full bg-posture-healthy-tint px-2.5 py-0.5 text-meta font-medium text-posture-healthy'
                    : p.status === 'publishing'
                      ? 'rounded-full bg-posture-at-risk-tint px-2.5 py-0.5 text-meta font-medium text-posture-at-risk'
                      : 'rounded-full bg-ai-surface px-2.5 py-0.5 text-meta font-medium text-ai-ink'
                }
              >
                {capitalize(p.status)}
              </span>
              <span className="font-mono text-meta text-ink-500">
                {p.published_at ? formatDate(p.published_at) : formatDate(p.created_at)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function pulsePostureToStatus(p: PulsePosture): PostureStatus {
  switch (p) {
    case 'green':
      return 'healthy';
    case 'yellow':
      return 'at-risk';
    case 'red':
      return 'critical';
  }
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

// Silence unused-import warnings via dummy references in case
// future maintenance wires the symbols.
export type { PulseCitation };
export { ContractPulseTab };
